import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useQrScanner } from '../../../hooks/useQrScanner';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Camera, CheckCircle, AlertTriangle } from 'lucide-react';

export const QrScannerDialog = ({ open, onOpenChange, qrScannerRef, scanResult, validating, onScanResult, onRescan }) => {
  const containerRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const { start: startQr, stop: stopQr } = useQrScanner();

  useEffect(() => {
    if (!open || scanResult) return;
    let alive = true;
    setCameraError('');
    setCameraReady(false);

    const boot = async () => {
      await new Promise(r => setTimeout(r, 500));
      if (!alive) return;
      try {
        await startQr('pm-qr-container', (text) => {
          if (!alive) return;
          stopQr().then(() => { if (alive) onScanResult(text); });
        }, { fps: 8, qrbox: { width: 220, height: 220 } });
        if (alive) setCameraReady(true);
      } catch (err) {
        console.error('QR camera error:', err);
        if (alive) setCameraError('Nu se poate accesa camera. Verificati permisiunile.');
      }
    };
    boot();

    return () => {
      alive = false;
      stopQr();
    };
  }, [open, scanResult]);

  const handleRescan = async () => {
    await stopQr();
    setCameraReady(false);
    setCameraError('');
    onRescan();
  };

  const handleClose = async (val) => {
    if (!val) await stopQr();
    onOpenChange(val);
  };

  const showScanning = !scanResult;
  const showResult = !!scanResult;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm p-0 overflow-hidden">
        <DialogHeader className="bg-slate-900 text-white px-4 py-3 space-y-0">
          <DialogTitle className="text-sm font-bold text-white flex items-center gap-2">
            <Camera className="w-5 h-5" /> Scanner QR - Validare Preventiva
          </DialogTitle>
          <p className="text-[11px] text-slate-300">Scanati codul QR de pe utilaj</p>
        </DialogHeader>

        <div className="p-4" style={{ display: showScanning ? 'block' : 'none' }}>
          <div id="pm-qr-container"
            className="w-full rounded-lg overflow-hidden bg-black"
            style={{ minHeight: '280px' }}
            data-testid="qr-scanner-viewport" />
          {!cameraReady && !cameraError && showScanning && (
            <div className="text-center mt-3">
              <div className="w-8 h-8 border-3 border-blue-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-2" />
              <p className="text-xs text-slate-400">Se porneste camera...</p>
            </div>
          )}
          {cameraError && (
            <div className="text-center mt-3 p-3 bg-red-50 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-500 mx-auto mb-1" />
              <p className="text-xs text-red-600">{cameraError}</p>
            </div>
          )}
          {cameraReady && !cameraError && showScanning && (
            <p className="text-xs text-slate-500 text-center mt-3">Indreptati camera spre codul QR al utilajului</p>
          )}
        </div>

        {showResult && (
          <div className="p-5">
            {validating ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-slate-600">Se valideaza...</p>
                <p className="text-xs text-slate-400 mt-1 font-mono">{scanResult.text}</p>
              </div>
            ) : scanResult.status === 'success' ? (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h4 className="text-base font-bold text-emerald-700">Validare reusita!</h4>
                {scanResult.equipment && <p className="text-sm text-slate-700 mt-1 font-medium">{scanResult.equipment}</p>}
                <p className="text-sm text-slate-600 mt-1">{scanResult.message}</p>
                {scanResult.count > 0 && (
                  <Badge className="bg-emerald-100 text-emerald-700 mt-2">{scanResult.count} intrari validate</Badge>
                )}
                <div className="flex gap-2 mt-5">
                  <Button variant="outline" className="flex-1" onClick={handleRescan}>
                    <Camera className="w-4 h-4 mr-1" /> Alt utilaj
                  </Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => handleClose(false)}>Inchide</Button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-3">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <h4 className="text-base font-bold text-red-700">Eroare</h4>
                <p className="text-sm text-slate-600 mt-1">{scanResult.message}</p>
                <p className="text-xs text-slate-400 mt-1 font-mono">{scanResult.text}</p>
                <div className="flex gap-2 mt-5">
                  <Button variant="outline" className="flex-1" onClick={handleRescan}>
                    <Camera className="w-4 h-4 mr-1" /> Rescaneaza
                  </Button>
                  <Button variant="ghost" className="flex-1" onClick={() => handleClose(false)}>Inchide</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
